import { clsx } from "clsx";
import { ChevronDownIcon, XIcon } from "lucide-react";
import type { FocusEvent, KeyboardEvent, MouseEvent } from "react";
import { useCallback, useMemo, useRef } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useTranslations } from "../../i18n";
import { textFieldButtonSize } from "../../utils";
import { IconButton } from "../Button/IconButton";
import { useDropdownContext, useDropdownControlProps } from "../Dropdown";
import { FieldAccessory } from "../FieldAccessory";
import { Spinner } from "../Spinner";
import type { ISpinnerSize } from "../Spinner/types";
import { Tag } from "../Tag";
import type { ITagSize } from "../Tag/types";
import { TruncatedContent } from "../TruncatedContent";
import { useSelectContext } from "./context";
import type { ISelectSize, ISelectTriggerProps } from "./types";

const spinnerSizeMap: Record<ISelectSize, ISpinnerSize> = {
  sm: "xs",
  md: "sm",
  lg: "md",
};

const tagSize: Record<ISelectSize, ITagSize> = {
  sm: "sm",
  md: "md",
  lg: "lg",
};

/**
 * The box positions the panel and takes pointer events. The `combobox` role
 * sits on an inner element, so the tag remove buttons and the clear button
 * are its siblings, never its descendants.
 */
export const SelectTrigger = ({ ref, ...props }: ISelectTriggerProps) => {
  const {
    className,
    invalid,
    disabled,
    focused,
    loading,
    placeholder,
    displayValue,
    showClearButton,
    onClear,
    onRenderAnchor,
    multiple,
    value,
    options,
    onSelect,
    ...contextProps
  } = useSelectContext();
  const isMobile = useMediaQuery("(max-width: 639px)");
  const size: ISelectSize = contextProps.size || (isMobile ? "lg" : "md");
  const { t } = useTranslations();
  const { open } = useDropdownContext();
  const controlProps = useDropdownControlProps();
  const comboboxRef = useRef<HTMLDivElement>(null);

  const selectedOptions = useMemo(() => {
    if (!value) {
      return [];
    }

    const valueArray = Array.isArray(value) ? value : [value];
    return options.filter((opt) => valueArray.includes(opt.id));
  }, [value, options]);

  const rootClass = clsx(
    "nuco-field nuco-select__trigger",
    `nuco-select__trigger--${size}`,
    disabled && "nuco-field--disabled",
    invalid && "nuco-field--invalid nuco-color-danger",
    (open || focused) && "nuco-field--focused",
    className,
  );

  const triggerClass = onRenderAnchor ? className : rootClass;

  const handleClearClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onClear?.();
      comboboxRef.current?.focus();
    },
    [onClear],
  );

  // The box is focusable only so a label click or `focus()` lands somewhere; pass it on.
  const handleBoxFocus = useCallback((e: FocusEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      comboboxRef.current?.focus();
    }
  }, []);

  const handleClearMouseDown = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handleClearKeyDown = useCallback((e: KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  const dataAttributes = Object.fromEntries(
    Object.entries(contextProps).filter(([key]) => key.startsWith("data-")),
  );

  const placeholderJSX = (
    <span className="nuco-select__trigger-placeholder nuco-text nuco-text--md">
      {placeholder}
    </span>
  );

  const comboboxElement = (
    <div
      {...controlProps}
      ref={comboboxRef}
      className={clsx(
        "nuco-select__combobox",
        // A custom anchor draws its own ring; a field lights its box instead.
        onRenderAnchor
          ? "nuco-select__combobox--custom"
          : "nuco-field__control",
      )}
      aria-busy={loading ? "true" : undefined}
    >
      {onRenderAnchor ? (
        onRenderAnchor(open)
      ) : multiple ? (
        selectedOptions.length > 0 ? null : (
          placeholderJSX
        )
      ) : displayValue ? (
        <TruncatedContent
          className="nuco-select__trigger-value nuco-text nuco-text--md"
          content={displayValue}
        />
      ) : (
        placeholderJSX
      )}
    </div>
  );

  return (
    <div
      {...props}
      {...dataAttributes}
      ref={ref}
      className={triggerClass}
      tabIndex={-1}
      onFocus={handleBoxFocus}
    >
      {onRenderAnchor ? (
        comboboxElement
      ) : (
        <>
          <div className="nuco-select__trigger-inner">
            {multiple &&
              selectedOptions.map((option) => (
                <Tag
                  key={option.id}
                  id={option.id}
                  text={option.text}
                  startIcon={option.icon}
                  color="neutral"
                  size={tagSize[size]}
                  onRemove={() => onSelect(option.id)}
                  disabled={disabled}
                />
              ))}
            {comboboxElement}
          </div>

          <div className="nuco-select__trigger-actions">
            {showClearButton && (
              <IconButton
                variant="ghost"
                size={textFieldButtonSize[size]}
                color="neutral"
                onClick={handleClearClick}
                onKeyDown={handleClearKeyDown}
                onMouseDown={handleClearMouseDown}
                onPointerDown={(e) => e.stopPropagation()}
                content={<XIcon />}
                aria-label={t("ui.select.clearSelection")}
              />
            )}

            {!disabled && loading && (
              <FieldAccessory size={size}>
                <Spinner color="neutral" size={spinnerSizeMap[size]} />
              </FieldAccessory>
            )}
            {!disabled && !loading && (
              <IconButton
                as="span"
                variant="ghost"
                size={textFieldButtonSize[size]}
                color="neutral"
                aria-hidden="true"
              >
                <ChevronDownIcon
                  className={clsx(
                    "nuco-select__chevron",
                    open && "nuco-select__chevron--rotate",
                  )}
                />
              </IconButton>
            )}
          </div>
        </>
      )}
    </div>
  );
};
